import { inject, Injectable, signal } from '@angular/core';
import { CampaignItemsFormArray, CampaignRulesFormArray, CampaignsFields, CampaignsFormGroups, CampaignsValidators, CampaignsWithCustomFields } from '../models/fidelity-campaign.models';
import { CustomFormArray, CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';
import { ChainCampaigns, ChainCampaignsItems, ChainPrizes, ChainRules } from 'tilby-models';
import { FidelityDataCatalogService } from './fidelity-data-catalog.service';

@Injectable()
export class FidelityCampaignFormService {

  private readonly fidelityDataCatalogService = inject(FidelityDataCatalogService);

  private campaignForm!: CampaignsFormGroups;
  
  get form() {
    return this.campaignForm;
  }

  campaignFormReady = signal(false);

  campaignRules: Array<ChainRules> = [];
  campaignProducts: Array<ChainCampaignsItems> = [];
  campaignPrizes: Array<ChainPrizes> = [];

  validators: CampaignsValidators = {
    nameMaxLength: 255,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(campaign: CampaignsFields | null, hiddenFields?:Array<keyof CampaignsFields>) {
    this.campaignFormReady.set(false);
    this.campaignForm = new CustomFormGroup<CampaignsWithCustomFields>({
      id: new CustomFormControl(
        campaign?.id || null
      ),
      name: new CustomFormControl(
        campaign?.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }
      ),
      active: new CustomFormControl(
        campaign? campaign.active : true
      ),
      from_date: new CustomFormControl(
        campaign?.from_date || null
      ),
      to_date: new CustomFormControl(
        campaign?.to_date || null
      ),
      rules_application_on_prizes: new CustomFormControl(
        campaign?.rules_application_on_prizes || null
      ),
      rules: new CustomFormArray<CustomFormGroup<CampaignRulesFormArray>>([]),
      items: new CustomFormArray<CustomFormGroup<CampaignItemsFormArray>>([])
    });

    this.initRules(campaign?.rules || []);
    this.initItems(campaign?.items || []);

    this.campaignFormReady.set(true);
    return this.campaignForm;
  }

  initRules(rules: ChainRules[]) {
    rules.forEach((rule: ChainRules) => {
      this.rulesArray?.push(new CustomFormGroup<CampaignRulesFormArray>({
        id: new CustomFormControl(
          rule.id || null
        ),
        name: new CustomFormControl(
          rule.name || null
        )
      }))
    })
  }

  addNewRule() {
    this.rulesArray?.push(new CustomFormGroup<CampaignRulesFormArray>({
      id: new CustomFormControl(
        null
      ),
      name: new CustomFormControl(
        null
      )
    }))
  }

  removeRule(index: number) {
    this.rulesArray?.removeAt(index);
  }

  initItems(items: ChainCampaignsItems[]) {
    items.forEach((item: ChainCampaignsItems) => {
      this.itemsArray?.push(new CustomFormGroup<CampaignItemsFormArray>({
        id: new CustomFormControl(
          item.id || null
        ),
        name: new CustomFormControl(
          item.name || null
        ),
        sku: new CustomFormControl(
          item.sku || null
        ),
        points: new CustomFormControl(
          item.points || null
        )
      }))
    })
  }

  addNewItem() {
    this.itemsArray?.push(new CustomFormGroup<CampaignItemsFormArray>({
      id: new CustomFormControl(
        null
      ),
      name: new CustomFormControl(
        null
      ),
      sku: new CustomFormControl(
        null
      ),
      points: new CustomFormControl(
        null
      )
    }))
  }

  removeItem(index: number) {
    this.itemsArray?.removeAt(index);
  }

  prepareDataForSave(): any {
    const formValue = this.campaignForm.getRawValue();
    const campaignToSend = structuredClone(formValue);

    campaignToSend.from_date = formValue.from_date ? new Date(formValue.from_date).toISOString() : null;
    campaignToSend.to_date = formValue.to_date ? new Date(formValue.to_date).toISOString() : null;

    const rulesMap = this.fidelityDataCatalogService.getDataByKey('rulesMap') as Record<string | number, ChainRules>;
    campaignToSend.rules = formValue.rules?.filter((r: any) => !!r.id)
      .map((r: any) => rulesMap[r.id]);

    campaignToSend.items = formValue.items?.filter((item: any) => {
      return !!(item.sku && item.name && item.points);
    });

    return campaignToSend;
  }

  prepareTogglePayload(campaign: ChainCampaigns, newStatus: boolean): any {
    const payload = structuredClone(campaign);
    payload.active = newStatus;
    
    return payload;
  }

  get id() {
    return this.campaignForm.controls.id;
  }

  get name() {
    return this.campaignForm.controls.name;
  }

  get active() {
    return this.campaignForm.controls.active;
  }

  get from_date() {
    return this.campaignForm.controls.from_date;
  }

  get to_date() {
    return this.campaignForm.controls.to_date;
  }

  get rules_application_on_prizes() {
    return this.campaignForm.controls.rules_application_on_prizes;
  }

  get rulesArray() {
    return this.campaignForm.controls.rules;
  }

  getIdRule(index: number) {
    return this.rulesArray?.at(index).controls.id;
  }

  getNameRule(index: number) {
    return this.rulesArray?.at(index).controls.name;
  }

  get itemsArray() {
    return this.campaignForm.controls.items;
  }

  getIdItem(index: number) {
    return this.itemsArray?.at(index).controls.id;
  }

  getNameItem(index: number) {
    return this.itemsArray?.at(index).controls.name;
  }

  getSkuItem(index: number) {
    return this.itemsArray?.at(index).controls.sku;
  }

  getPointsItem(index: number) {
    return this.itemsArray?.at(index).controls.points;
  }
}
